/**
 * The usage card — pricing honesty rendered as UI.
 *
 * `billing-entitlements.md` §11 makes a live usage view a requirement on EVERY plan, Free included,
 * and `ux.md` §8 fixes one phrase it must carry inline rather than in a tooltip: "unsigned sends:
 * not counted". That sentence is the whole difference between a customer who hesitates before
 * sending and one who does not, and it is true of this product in a way it is not true of most.
 *
 * The number comes from `GET /v1/usage`, which is the same call `POST …/send` makes before it
 * refuses. §11 states the constraint as a parenthetical — "one source, so the UI can never disagree
 * with the gate" — and one source means one endpoint, not two that agree today.
 *
 * §11's MVP list also asks for overage in currency and a month-to-date bill. Neither exists: there
 * is no payment method, no subscription and no aggregator (ADR 0044). The card says so in words
 * instead of rendering a `$0.00` the product would be inventing.
 *
 * ## Why a ring
 *
 * A number on its own answers "how many"; the question a person actually has at the top of a
 * dashboard is "how much of the month is left", and that is a proportion. The ring answers it
 * pre-attentively — from across a desk, before reading — and the figure inside it answers the
 * first question at the same time. It is drawn only when there IS a ceiling: on an unlimited plan
 * there is no proportion to draw, and a full ring would be a picture of a limit that does not
 * exist.
 */
import { Callout, Card, ErrorState, Icon, LoadingState, Meter } from '@flowza/ui'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../api/client'
import { describe as messageOf } from './apiErrors'

export type FeatureUsage = {
  readonly feature: string
  readonly used: number
  readonly included: number | null
  readonly remaining: number | null
  readonly unlimited: boolean
  readonly unsigned_sends_counted: boolean
}

export type UsageResource = {
  readonly object: string
  readonly period_start: string
  readonly period_end: string
  readonly billable: boolean
  readonly data: readonly FeatureUsage[]
}

export type UsageClient = { read(): Promise<UsageResource> }

export const apiUsageClient: UsageClient = { read: () => api<UsageResource>('/v1/usage') }

/** "1 October" — the day the allowance comes back, in the reader's own locale. */
export function resetLabel(periodEnd: string): string {
  const at = new Date(periodEnd)
  return Number.isNaN(at.getTime())
    ? periodEnd
    : at.toLocaleDateString(undefined, { day: 'numeric', month: 'long' })
}

/** Stats live in Reports (ux.md §6.1); this card is the meter, not the analysis. */
const REPORTS_LINK = (
  <Link
    to="/app/reports"
    className="text-accent inline-flex items-center gap-1 font-medium hover:underline"
  >
    View reports
    <Icon name="arrow-right" size={14} />
  </Link>
)

export function UsageCard({ client = apiUsageClient }: { readonly client?: UsageClient }) {
  const [state, setState] = useState<
    | { readonly kind: 'loading' }
    | { readonly kind: 'ready'; readonly usage: UsageResource }
    | { readonly kind: 'error'; readonly message: string }
  >({ kind: 'loading' })

  useEffect(() => {
    let cancelled = false
    client
      .read()
      .then((usage) => {
        if (!cancelled) setState({ kind: 'ready', usage })
      })
      .catch((error: unknown) => {
        if (!cancelled) setState({ kind: 'error', message: messageOf(error) })
      })
    return () => {
      cancelled = true
    }
  }, [client])

  if (state.kind === 'loading') {
    return (
      <Card title="This month">
        <LoadingState label="Loading usage" />
      </Card>
    )
  }
  if (state.kind === 'error') {
    return (
      <Card title="This month">
        <ErrorState title="Usage could not load" body={state.message} />
      </Card>
    )
  }

  const envelopes = state.usage.data.find((row) => row.feature === 'envelopes')
  if (envelopes === undefined) {
    return (
      <Card title="This month">
        <p className="text-text-muted mb-0 text-sm">
          This cell reports no envelope usage, which means the meter is not wired here.
        </p>
      </Card>
    )
  }

  const included = envelopes.included
  // A ceiling worth drawing: a plan that is unlimited has none, and a plan reporting `0` included
  // would divide by zero — both draw the plain figure instead.
  const metered = !envelopes.unlimited && included !== null && included > 0
  const spent = metered ? envelopes.used / included : 0
  const tone = envelopes.remaining === 0 ? 'warning' : spent >= 0.8 ? 'warning' : 'accent'

  return (
    <Card title="This month" action={REPORTS_LINK}>
      <div className="flex items-center gap-4">
        {metered ? (
          <Meter
            value={envelopes.used}
            max={included}
            tone={tone}
            label={`${envelopes.used} of ${included} completed envelopes used this month`}
          >
            <span className="text-text-primary text-xl font-semibold tabular-nums leading-none">
              {envelopes.used}
            </span>
            <span className="text-text-muted mt-1 text-xs tabular-nums">of {included}</span>
          </Meter>
        ) : null}
        <div className="min-w-0">
          {metered ? null : (
            <p className="text-text-primary m-0 text-3xl font-semibold tabular-nums leading-none">
              {envelopes.used}
            </p>
          )}
          {/* Uppercased in CSS, not in the string: `text-transform` leaves the DOM text — and so
              the accessible name and anything asserting on it — as the sentence a person reads
              aloud, while the eye gets a column label rather than a phrase floating mid-card. */}
          <p className="text-text-muted m-0 mt-1 text-xs font-medium uppercase tracking-wide">
            completed envelope{envelopes.used === 1 ? '' : 's'}
          </p>
          {metered ? (
            <ul className="text-text-muted m-0 mt-2 flex list-none flex-col gap-1 p-0 text-sm">
              <li className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className={`size-2 shrink-0 rounded-full ${
                    tone === 'warning' ? 'bg-warning' : 'bg-accent'
                  }`}
                />
                {envelopes.used} counted so far
              </li>
              {envelopes.remaining === null ? null : (
                <li className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className="bg-border-strong size-2 shrink-0 rounded-full"
                  />
                  {envelopes.remaining} still included this month
                </li>
              )}
            </ul>
          ) : (
            <p className="text-text-muted m-0 mt-1 text-sm">There is no limit on this plan.</p>
          )}
        </div>
      </div>

      {/*
        ux.md §8 asks for this sentence INLINE. It is not a caveat: on this product a send that is
        never signed costs the sender nothing, and a customer who does not know that sends less.
      */}
      <p className="text-text-muted m-0 mt-4 text-sm">
        Unsigned sends are not counted. Drafts, declines, voids and corrections are free — only a
        completed envelope counts against the month.
      </p>

      {!envelopes.unlimited && envelopes.remaining === 0 && (
        <div className="mt-4">
          <Callout tone="warning" title="You have used this month's allowance">
            {/* One string rather than three nodes: a screen reader reads it as one sentence, and a
                test can assert the sentence rather than the fragments it happens to be built from. */}
            {`Envelopes already sent can still be signed and completed. New sends resume on ${resetLabel(
              state.usage.period_end,
            )}.`}
          </Callout>
        </div>
      )}

      {state.usage.billable ? null : (
        <p className="text-text-muted m-0 mt-3 text-sm">
          Nothing is billed. There is no payment method on this account and no invoice has ever been
          raised; the count is kept so that any future price applies to new work rather than to work
          you have already done.
        </p>
      )}
    </Card>
  )
}
